import type { SupportAdmin } from '@siteday/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext } from 'react';
import { useNavigate } from 'react-router';

import * as supportApi from '@/api/support';
import { qk } from '@/api/support';

/** GET /support/auth/me on load; the `sd_support` cookie decides whether we are signed in. */
export function useMeQuery() {
  return useQuery({ queryKey: qk.me(), queryFn: supportApi.me, retry: false, staleTime: 5 * 60_000 });
}

const SessionCtx = createContext<SupportAdmin | null>(null);
export const SessionProvider = SessionCtx.Provider;

export function useSession(): SupportAdmin {
  const s = useContext(SessionCtx);
  if (!s) throw new Error('useSession outside AuthGuard');
  return s;
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: supportApi.login,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: qk.me() });
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: supportApi.logout,
    onSettled: () => {
      qc.clear();
      navigate('/sign-in', { replace: true });
    },
  });
}
